import { useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, Copy, ImagePlus, Loader2, LogOut, MessageCircle, ShieldAlert, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const STATUS_LABELS = {
  em_analise: "Em análise",
  aprovado: "Aprovado",
  aguardando_pagamento: "Aguardando pagamento",
  pago: "Pago",
  concluido: "Concluído",
  cancelado: "Cancelado",
};
const STATUS_COLORS = {
  em_analise: "text-brand border-brand/50",
  aprovado: "text-brand border-brand/50",
  aguardando_pagamento: "text-mist border-metal",
  pago: "text-paper border-brand bg-brand/10",
  concluido: "text-paper border-metal",
  cancelado: "text-steel border-metal line-through",
};

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("sagarelo_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem("sagarelo_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Falha no login");
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <main className="honeycomb flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login">
        <form onSubmit={doLogin} className="card-industrial w-full max-w-sm p-8">
          <div className="mb-8 flex items-center gap-3">
            <img src="/images/logo-icon.png" alt="Sagarelo" className="h-11 w-11 rounded-xl border border-metal object-cover" />
            <div>
              <p className="font-display text-sm font-extrabold text-paper">PAINEL SAGARELO</p>
              <p className="text-[0.65rem] font-bold tracking-[0.2em] text-brand">ÁREA RESTRITA</p>
            </div>
          </div>
          <input className="field-industrial mb-3" type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
          <input className="field-industrial mb-5" type="password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
          {loginError && <p className="mb-4 rounded-xl border border-brand/40 bg-ink p-3 text-xs font-semibold text-brand" data-testid="admin-login-error">{loginError}</p>}
          <button type="submit" disabled={loading} className="btn-industrial w-full" data-testid="admin-login-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            ENTRAR
          </button>
        </form>
      </main>
    );
  }

  return <AdminDashboard token={token} onLogout={() => { localStorage.removeItem("sagarelo_admin_token"); setToken(""); }} />;
}

function AdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("pedidos");
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [policy, setPolicy] = useState({ text: "", configured: false });
  const [policyDraft, setPolicyDraft] = useState("");
  const [pix, setPix] = useState({ key_value: "", beneficiary: "", configured: false });
  const [pixDraft, setPixDraft] = useState({ key_value: "", beneficiary: "" });
  const [busy, setBusy] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const [o, s, p, px] = await Promise.all([
        axios.get(`${API}/api/admin/orders`, auth),
        axios.get(`${API}/api/hire/services`),
        axios.get(`${API}/api/admin/policy`, auth),
        axios.get(`${API}/api/admin/pix`, auth),
      ]);
      setOrders(o.data);
      setServices(s.data);
      setPolicy(p.data);
      setPolicyDraft(p.data.text);
      setPix(px.data);
      setPixDraft({ key_value: px.data.key_value, beneficiary: px.data.beneficiary });
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (order) => {
    setBusy(order.id);
    try {
      const { data } = await axios.post(`${API}/api/admin/orders/${order.id}/approve`, { origin_url: window.location.origin }, auth);
      await load();
      if (data.payment_url) {
        window.prompt("Link de pagamento gerado. Copie e envie ao cliente:", data.payment_url);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Falha ao aprovar pedido");
    }
    setBusy("");
  };

  const setStatus = async (order, status) => {
    setBusy(order.id);
    await axios.post(`${API}/api/admin/orders/${order.id}/status`, { status }, auth).catch(() => {});
    await load();
    setBusy("");
  };

  const uploadServiceImage = async (serviceId, file) => {
    if (!file) return;
    setBusy(serviceId);
    const data = new FormData();
    data.append("file", file);
    await axios.post(`${API}/api/admin/services/${serviceId}/image`, data, auth).catch((e) => alert(e.response?.data?.detail || "Falha no upload"));
    await load();
    setBusy("");
  };

  const removeServiceImage = async (serviceId) => {
    setBusy(serviceId);
    await axios.delete(`${API}/api/admin/services/${serviceId}/image`, auth).catch(() => {});
    await load();
    setBusy("");
  };

  const savePolicy = async () => {
    setBusy("policy");
    await axios.put(`${API}/api/admin/policy`, { text: policyDraft }, auth).catch(() => {});
    await load();
    setBusy("");
  };

  const savePix = async () => {
    setBusy("pix");
    await axios.put(`${API}/api/admin/pix`, pixDraft, auth).catch(() => {});
    await load();
    setBusy("");
  };

  const clientPhone = (order) => {
    const phone = (order.whatsapp || "").replace(/\D/g, "");
    return phone.startsWith("55") ? phone : `55${phone}`;
  };

  const waShare = (order) => {
    const msg = `Olá ${order.nome}! Aqui é da Sagarelo Refrigeração. Seu pedido #${order.id.slice(0, 8).toUpperCase()} (${order.service_title}) foi aprovado na análise de viabilidade. Segue o link para pagamento: ${order.payment_url}`;
    window.open(`https://wa.me/${clientPhone(order)}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const waSharePix = (order) => {
    const msg = [
      `Olá ${order.nome}! Aqui é da Sagarelo Refrigeração.`,
      `Seu pedido #${order.id.slice(0, 8).toUpperCase()} (${order.service_title}) foi aprovado na análise de viabilidade.`,
      "",
      "Dados para pagamento via Pix:",
      `Chave CNPJ: ${pix.key_value}`,
      `Favorecido: ${pix.beneficiary}`,
      `Valor: ${brl(order.total)}`,
      "",
      "Após o pagamento, nos envie o comprovante por aqui para confirmarmos o agendamento.",
    ].join("\n");
    window.open(`https://wa.me/${clientPhone(order)}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <main className="honeycomb min-h-screen bg-ink px-5 py-10 md:px-8" data-testid="admin-dashboard">
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/images/logo-icon.png" alt="Sagarelo" className="h-11 w-11 rounded-xl border border-metal object-cover" />
            <div>
              <p className="font-display text-base font-extrabold text-paper">PAINEL SAGARELO</p>
              <p className="text-[0.65rem] font-bold tracking-[0.2em] text-brand">CONTRATAÇÃO DE INSTALAÇÃO</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs font-bold tracking-wide text-steel transition-colors hover:text-brand">Ver site</a>
            <button onClick={onLogout} className="btn-outline-industrial !px-4 !py-2" data-testid="admin-logout">
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>

        {!policy.configured && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-brand/50 bg-graphite p-4" data-testid="admin-policy-warning">
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" />
            <p className="text-xs leading-relaxed text-mist">
              <span className="font-bold text-brand">Atenção:</span> a política para casos de inviabilidade ainda não foi preenchida.
              Preencha na aba <button onClick={() => setTab("politica")} className="font-bold text-brand underline">Política</button> antes de gerar cobranças.
            </p>
          </div>
        )}

        <div className="mb-6 flex gap-2">
          {[["pedidos", `PEDIDOS (${orders.length})`], ["servicos", "SERVIÇOS"], ["politica", "POLÍTICA E PIX"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`rounded-full border px-5 py-2.5 font-display text-[0.68rem] font-extrabold tracking-[0.14em] transition-colors duration-300 ${tab === key ? "border-brand bg-brand text-ink" : "border-metal bg-graphite text-mist hover:border-brand/60"}`}
              data-testid={`admin-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "pedidos" && (
          <div className="space-y-4" data-testid="admin-orders">
            {orders.length === 0 && <p className="card-industrial p-8 text-center text-sm text-steel">Nenhum pedido ainda.</p>}
            {orders.map((o) => (
              <article key={o.id} className="card-industrial p-5" data-testid={`admin-order-${o.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-sm font-extrabold text-paper">
                      #{o.id.slice(0, 8).toUpperCase()} — {o.service_title}
                    </p>
                    <p className="mt-1 text-xs text-mist">
                      {o.nome} • {o.whatsapp}
                      {o.brand && ` • ${o.brand}`}
                      {o.andar && ` • ${o.andar}º andar`} • {o.capacidade}
                    </p>
                    <p className="mt-1 text-xs text-steel">{o.endereco || o.empresa}</p>
                    <p className="mt-2 text-xs text-mist">
                      {o.method ? (o.method === "pix" ? "Pix" : o.method === "debito" ? "Débito" : `Crédito ${o.installments}x`) : "Sob consulta"}
                      {o.total != null && <> — <span className="font-bold text-brand">{brl(o.total)}</span></>}
                      {o.installments > 1 && ` (${o.installments}x de ${brl(o.installment_values[0])})`}
                    </p>
                    {o.photos?.length > 0 && (
                      <div className="mt-3 flex gap-2">
                        {o.photos.map((p) => (
                          <a key={p} href={`${API}/api/hire/files/${p}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${API}/api/hire/files/${p}`} alt="Foto do local" className="h-14 w-14 rounded-lg border border-metal object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`rounded-full border px-3 py-1 text-[0.62rem] font-extrabold tracking-[0.14em] ${STATUS_COLORS[o.status] || "border-metal text-mist"}`}>
                      {STATUS_LABELS[o.status] || o.status}
                    </span>
                    <div className="flex flex-wrap justify-end gap-2">
                      {(o.status === "em_analise" || o.status === "aprovado") && o.method && (
                        <button onClick={() => approve(o)} disabled={busy === o.id} className="btn-industrial !px-4 !py-2 !text-[0.62rem]" data-testid={`admin-approve-${o.id.slice(0, 8)}`}>
                          {busy === o.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <BadgeCheck className="h-3.5 w-3.5" />}
                          {o.method === "pix" ? "APROVAR VIABILIDADE" : "APROVAR E GERAR LINK"}
                        </button>
                      )}
                      {o.payment_url && o.status === "aguardando_pagamento" && o.method !== "pix" && (
                        <>
                          <button onClick={() => { navigator.clipboard.writeText(o.payment_url); }} className="btn-outline-industrial !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-copy-${o.id.slice(0, 8)}`}>
                            <Copy className="h-3.5 w-3.5" /> COPIAR LINK
                          </button>
                          <button onClick={() => waShare(o)} className="btn-outline-industrial !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-wa-${o.id.slice(0, 8)}`}>
                            <MessageCircle className="h-3.5 w-3.5" /> ENVIAR NO WHATSAPP
                          </button>
                        </>
                      )}
                      {o.status === "aguardando_pagamento" && o.method === "pix" && (
                        <>
                          <button onClick={() => waSharePix(o)} className="btn-outline-industrial !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-wapix-${o.id.slice(0, 8)}`}>
                            <MessageCircle className="h-3.5 w-3.5" /> ENVIAR DADOS PIX
                          </button>
                          <button onClick={() => setStatus(o, "pago")} disabled={busy === o.id} className="btn-industrial !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-confirm-pix-${o.id.slice(0, 8)}`}>
                            <BadgeCheck className="h-3.5 w-3.5" /> CONFIRMAR RECEBIMENTO
                          </button>
                        </>
                      )}
                      {o.status !== "cancelado" && o.status !== "concluido" && o.status !== "pago" && (
                        <button onClick={() => setStatus(o, "cancelado")} className="text-[0.62rem] font-bold tracking-wide text-steel transition-colors hover:text-brand" data-testid={`admin-cancel-${o.id.slice(0, 8)}`}>
                          Cancelar
                        </button>
                      )}
                      {o.status === "pago" && (
                        <button onClick={() => setStatus(o, "concluido")} className="btn-outline-industrial !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-finish-${o.id.slice(0, 8)}`}>
                          Marcar concluído
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {tab === "servicos" && (
          <div className="grid gap-5 md:grid-cols-2" data-testid="admin-services">
            {services.map((s) => (
              <article key={s.id} className="card-industrial overflow-hidden" data-testid={`admin-service-${s.id}`}>
                <div className="flex h-44 items-center justify-center bg-coal">
                  {s.image_url ? (
                    <img src={`${API}${s.image_url}`} alt={s.title} className="h-full w-full object-cover" />
                  ) : (
                    <p className="px-6 text-center text-xs text-steel">Sem imagem</p>
                  )}
                </div>
                <div className="p-5">
                  <p className="font-display text-sm font-extrabold text-paper">{s.title}</p>
                  <div className="mt-4 flex gap-2">
                    <label className="btn-outline-industrial flex-1 cursor-pointer !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-upload-${s.id}`}>
                      {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                      {s.image_url ? "TROCAR" : "ENVIAR"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadServiceImage(s.id, e.target.files?.[0])} />
                    </label>
                    {s.image_url && (
                      <button onClick={() => removeServiceImage(s.id)} className="btn-outline-industrial !px-3 !py-2 !text-[0.62rem]" data-testid={`admin-remove-img-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {tab === "politica" && (
          <div className="space-y-5">
            <div className="card-industrial p-6 md:p-8" data-testid="admin-policy">
              <p className="font-display text-[0.72rem] font-extrabold tracking-[0.16em] text-brand">POLÍTICA EM CASO DE INVIABILIDADE</p>
              <p className="mt-2 text-xs leading-relaxed text-mist">
                Este texto é exibido ao cliente antes do envio do pedido. A geração de cobranças fica bloqueada até que ele seja preenchido.
              </p>
              <textarea
                className="field-industrial mt-5 resize-none"
                rows={6}
                placeholder="Ex.: Em caso de inviabilidade técnica após o pagamento, o valor será integralmente estornado ao cliente em até X dias úteis..."
                value={policyDraft}
                onChange={(e) => setPolicyDraft(e.target.value)}
                data-testid="admin-policy-textarea"
              />
              <button onClick={savePolicy} disabled={busy === "policy"} className="btn-industrial mt-4" data-testid="admin-policy-save">
                {busy === "policy" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
                SALVAR POLÍTICA
              </button>
              {policy.configured && <p className="mt-3 text-xs font-semibold text-brand">Política ativa — cobranças liberadas.</p>}
            </div>

            <div className="card-industrial p-6 md:p-8" data-testid="admin-pix">
              <p className="font-display text-[0.72rem] font-extrabold tracking-[0.16em] text-brand">DADOS PIX PARA RECEBIMENTO</p>
              <p className="mt-2 text-xs leading-relaxed text-mist">
                Pedidos no Pix não passam pelo gateway: após a aprovação da viabilidade, você envia estes dados ao cliente pelo WhatsApp e confirma o recebimento manualmente no painel.
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <input
                  className="field-industrial"
                  placeholder="Chave Pix (CNPJ, telefone, e-mail...)"
                  value={pixDraft.key_value}
                  onChange={(e) => setPixDraft((p) => ({ ...p, key_value: e.target.value }))}
                  data-testid="admin-pix-key"
                />
                <input
                  className="field-industrial"
                  placeholder="Nome do favorecido"
                  value={pixDraft.beneficiary}
                  onChange={(e) => setPixDraft((p) => ({ ...p, beneficiary: e.target.value }))}
                  data-testid="admin-pix-beneficiary"
                />
              </div>
              <button onClick={savePix} disabled={busy === "pix"} className="btn-industrial mt-4" data-testid="admin-pix-save">
                {busy === "pix" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
                SALVAR DADOS PIX
              </button>
              {pix.configured && (
                <p className="mt-3 text-xs font-semibold text-brand" data-testid="admin-pix-status">
                  Ativo — {pix.key_value} • {pix.beneficiary}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
